/**
 * Deepfake layer-2 injection-fingerprint capture (S5-4; AI_DESIGN §0.2 layer 2).
 *
 * The droom posts the client's own capture-integrity fingerprint (device/track enumeration,
 * getSettings/getCapabilities, MediaStreamTrack state, frame-timing — collected by
 * `media/captureFingerprint.ts`) to the session's injection-scan rail around getUserMedia. The server
 * forwards it to the in-VPC vCIP_ai own-injection-fingerprint scorer and echoes an advisory evidence row.
 *
 * The body is DEVICE metadata only — no frames, no biometrics, no location (that is the gated media-tap, not
 * this layer). The client sends no verdict and no IP; scoring is server-side. This is a probabilistic signal,
 * NOT proof of camera provenance (client.ts posture: client-supplied signals are evidence, never trusted as
 * decisions).
 */

import type { ClientCaptureFingerprint } from '@/media/captureFingerprint';
import { isMockMode, request } from './client';

/** One fired-signal line from the scorer's per-check breakdown (evidence, never a verdict). */
export interface InjectionSignal {
  readonly checkId: string;
  readonly detail: string;
}

/**
 * The advisory injection-scan row echoed by the backend. `advisory` is the honest layer-2 vocabulary;
 * `status` is its cockpit projection (INSUFFICIENT/UNAVAILABLE → ERROR). Both `certification`
 * (INTERIM_NOT_CERTIFIED) and `caveats` MUST travel with the score — a stripped stamp is dishonest evidence.
 */
export interface InjectionScanEvidence {
  readonly id: string;
  readonly sessionId: string;
  readonly attempt: number;
  readonly advisory: 'PASS' | 'REVIEW' | 'FAIL' | 'INSUFFICIENT' | 'UNAVAILABLE';
  readonly status: 'PASS' | 'REVIEW' | 'FAIL' | 'ERROR';
  readonly score: number | null;
  readonly threshold: number;
  readonly engine: string;
  readonly engineVersion: string;
  readonly certification: string | null;
  readonly counterfactual: string | null;
  readonly caveats: readonly string[];
  readonly signals: readonly InjectionSignal[];
  readonly sha256: string;
  readonly capturedAt: string;
}

/**
 * POST the fingerprint. Mock mode (no backend) resolves null WITHOUT collecting or posting — no injection
 * rail exists to bind a scan to, so we collect nothing (data minimisation), exactly like the geo rail.
 */
export async function postInjectionFingerprint(
  sessionId: string,
  fingerprint: ClientCaptureFingerprint,
): Promise<InjectionScanEvidence | null> {
  if (isMockMode()) return null;
  return request<InjectionScanEvidence>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/injection-scan`,
    { method: 'POST', body: fingerprint },
  );
}
